import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import URI from 'jsuri';
import PropTypes from 'prop-types';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import redirect from 'js/lib/coursera.redirect';
import { useRouter } from 'js/lib/useRouter';

import { Box, Button } from '@coursera/coursera-ui';

import { RECAPTCHA_ACTIONS, SIGNUP_RECAPTCHA_ID } from 'bundles/authentication/constants';
import TermsOfUseCheckbox from 'bundles/authentication/shared/TermsOfUseCheckbox';
import { useAddProfileConsent } from 'bundles/authentication/shared/hooks/useAddProfileConsent';
import useMailcheck from 'bundles/authentication/shared/hooks/useMailcheck';
import type { ReCAPTCHA } from 'bundles/authentication/shared/reCAPTCHA';
import reCAPTCHA from 'bundles/authentication/shared/reCAPTCHA';
import { shouldShowCheckboxTermsOfUse } from 'bundles/authentication/utils';
import { isMisspelledDomain } from 'bundles/authentication/utils/emailVerificationUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { ENROLL } from 'bundles/enroll/utils/enrollActionParams';
import GrowthAcquisitionExperiments from 'bundles/epic/clients/GrowthAcquisition';
import EmailTrackedButton from 'bundles/page/components/TrackedButton';
import {
  AUTH_FORM_IDS,
  AUTH_MODES,
  USER_EMAIL_REGEX_PATTERN,
  USER_NAME_REGEX_PATTERN,
  USER_PASSWORD_MAX_LENGTH,
  USER_PASSWORD_MIN_LENGTH,
  USER_PASSWORD_REGEX_PATTERN,
} from 'bundles/user-account/common/constants';
import AuthFormFooter from 'bundles/user-account/components/AuthFormFooter';
import EmailField from 'bundles/user-account/components/EmailField';
import ErrorMessage from 'bundles/user-account/components/ErrorMessage';
import NameField from 'bundles/user-account/components/NameField';
import PasswordField from 'bundles/user-account/components/PasswordField';
import signUpAPI from 'bundles/user-account/data/signUpApi';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';
import { getClientSideMessages, getResponseErrorCode } from 'bundles/user-account/utils/errorMessageUtils';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/user-account';

import 'css!./__styles__/SignupForm';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })(Button);

const NAME = AUTH_FORM_IDS.name;
const EMAIL = AUTH_FORM_IDS.email;
const PASSWORD = AUTH_FORM_IDS.password;
const CURRENT_AUTH_MODE = AUTH_MODES.signup;

type EventData = {
  namespace: {
    app: string;
    page: string;
  };
};

type Props = {
  isEnterprise: boolean;
  enableSSOLogin: boolean;
  redirectUrl?: string;
  verificationEmailRedirectUrl?: string;
};

type Context = {
  _eventData: EventData;
};

const SignupForm = (
  { redirectUrl, isEnterprise, enableSSOLogin, verificationEmailRedirectUrl }: Props,
  { _eventData }: Context
) => {
  const captchaRef = useRef<ReCAPTCHA | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const touCheckboxRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [password, setPassword] = useState('');
  const [isSignupBtnDisabled, setIsSignupBtnDisabled] = useState(false);
  const suggestion = useMailcheck(email);
  const router = useRouter();
  const showCheckboxTermsOfUse = shouldShowCheckboxTermsOfUse();
  const addProfileConsent = useAddProfileConsent();

  useEffect(() => {
    emailRef.current = document.getElementsByName('email')[0] as HTMLInputElement;
    touCheckboxRef.current = document.getElementsByName('touCheckbox')[0] as HTMLInputElement;
    const emailFromRouter = router.location.query.email;
    const prefilledEmail = emailFromRouter && decodeURI(emailFromRouter);
    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
  }, [router.location.query.email]);

  useEffect(() => {
    (function initializeCaptchaAndRefs() {
      const useEnterpriseCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaSignup');
      captchaRef.current = reCAPTCHA(SIGNUP_RECAPTCHA_ID, {
        _eventData,
        checkbox: useEnterpriseCheckbox,
        action: RECAPTCHA_ACTIONS.signup,
      });
    })();

    // We only want to initialize reCAPTCHA once, so we leave the dependency array empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignupError = (response: $TSFixMe) => {
    captchaRef.current?.reset();

    const receivedErrorCode = getResponseErrorCode(response?.responseJSON);

    setErrorCode(receivedErrorCode);
    setIsSignupBtnDisabled(false);
  };

  const dispatchEmailChange = (suggestedEmailDomain: string) => {
    if (emailRef.current) {
      const modifiedEmail = emailRef.current.value.split('@')[0] + '@' + suggestedEmailDomain;
      emailRef.current.value = modifiedEmail;
      setEmail(modifiedEmail);
    }
  };

  const onNameChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => setName(ev.target.value), []);
  const onEmailChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => setEmail(ev.target.value), []);
  const onPasswordChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => setPassword(ev.target.value), []);

  const getTranslations = (): { [name: string]: string } => {
    return {
      SIGN_UP: _t('Join for Free'),
      PASSWORD_SUBLABEL: _t('(Between #{minLength} and #{maxLength} characters)', {
        minLength: USER_PASSWORD_MIN_LENGTH,
        maxLength: USER_PASSWORD_MAX_LENGTH,
      }),
    };
  };

  const trySignUp = async () => {
    const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaSignup');

    const skipWelcomeEmail = isEnterprise;

    const emailSplit = email.split('@');
    const emailProvider = emailSplit[emailSplit.length - 1];

    if (isMisspelledDomain(emailProvider)) {
      emailRef.current?.setCustomValidity(
        _t('Looks like you misspelled the domain of your email address. Please try again.')
      );
      emailRef.current?.reportValidity();

      return false;
    }

    if (showCheckboxTermsOfUse && touCheckboxRef.current?.value !== 'true') {
      touCheckboxRef.current?.setCustomValidity(_t('Must accept Terms of Use and Privacy Notice.'));
      touCheckboxRef.current?.reportValidity();

      return false;
    }

    const token = await captchaRef.current?.getResponse();

    if (token === null) {
      return setErrorCode(isCheckbox ? 'recaptchaFailed' : '');
    }

    setIsSignupBtnDisabled(true);

    trackAuthAttempts(_eventData, 'signup');

    // Using api/register/v1 instead of /api/register/v1Ssr' so that we can fire successful sign up event to GTM on FE upon success
    // @ts-expect-error TS7016 TODO: token has incompatible type
    signUpAPI(email, name, password, token, redirectUrl, verificationEmailRedirectUrl, skipWelcomeEmail)
      .then((response) => {
        const redirectUrlBE = response?.redirectTo;
        const { query, pathname, search } = router.location;

        if (showCheckboxTermsOfUse) addProfileConsent();

        return Instrumentation.register().fin(() => {
          let defaultRedirectUrl;
          if (pathname.includes('frontpage-next')) {
            defaultRedirectUrl = new URI('/' + search).deleteQueryParam('authMode');
          } else {
            defaultRedirectUrl = new URI(pathname + search).deleteQueryParam('authMode');
          }

          if (!isEnterprise) {
            // Specifically disallowing modal use on signup when enrollment or financial aid
            // is also included in the params. This prevents the traditional Onboarding flow from
            // complicating the usecase enabled by the enrollment flow for onboarding.
            // When the enrollment param is appended to the URL, we know that a user was attempting
            // to enroll before being prompted to signup. As such, we make sure that this param
            // isn't present before starting with any of the redirection strategies for Onboarding.
            const isEnrollmentFlow = query?.action === ENROLL;
            const isStudentUpswell = query?.canContinue;
            const isFinaid = query?.aid === 'true';
            if (!(isEnrollmentFlow || isFinaid || isStudentUpswell)) {
              defaultRedirectUrl = new URI('/?showOnboardingModal=1&isNewUser=true');
            }
          }

          // let the query param or custom redirect url take precedence over any other computed redirectUrl
          // BE redirect url takes precedence over everything else besides an explicit  param-base override.
          // In the future, BE will have a whitelist of urls that the page can redirect to.
          redirect.setLocation(redirectUrl || redirectUrlBE || defaultRedirectUrl.toString());
        });
      })
      .catch((resp) => {
        handleSignupError(resp);
      });
  };

  const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaSignup');

  const TRANSLATIONS = getTranslations();
  const clientSideMessages = getClientSideMessages();

  let autoFocus: string = NAME;
  if (email && name) {
    autoFocus = PASSWORD;
  } else if (name) {
    autoFocus = EMAIL;
  }

  return (
    <Box rootClassName="rc-SignupForm" htmlAttributes={{ 'data-e2e': 'signup-form-wrapper' }} flexDirection="column">
      {errorCode && <ErrorMessage errorCode={errorCode} id="signup-form-error" />}
      {/* setting action to javascript:void(0) because of
        https://stackoverflow.com/questions/1818249/form-with-no-action-and-where-enter-does-not-reload-page */}
      {/* eslint-disable-next-line no-script-url */}
      <form action="javascript:void(0)" onSubmit={trySignUp} name="signup">
        <Box rootClassName="w-100 placeholder-contrast" flexDirection="column">
          <div className="m-b-1">
            <NameField
              defaultValue={name}
              value={name}
              pattern={USER_NAME_REGEX_PATTERN}
              title={clientSideMessages.NAME_VALIDATION}
              autoFocus={autoFocus === 'name'}
              onChange={onNameChange}
              aria-describedby="signup-form-error"
              aria-required={true}
              data-e2e="signup-name-input"
            />
          </div>
          <div className="m-b-1">
            <EmailField
              value={email}
              pattern={USER_EMAIL_REGEX_PATTERN}
              title={clientSideMessages.EMAIL_VALIDATION}
              autoFocus={autoFocus === 'email'}
              onChange={onEmailChange}
              aria-describedby="signup-form-error"
              aria-required={true}
              data-e2e="signup-email-input"
            />
            {suggestion && (
              <FormattedMessage
                className="email-warning"
                aria-live="polite"
                message="Did you mean {suggestedEmailDomain}?"
                suggestedEmailDomain={
                  <EmailTrackedButton
                    className="nostyle"
                    trackingName="sign-up-email-suggestion"
                    onClick={() => dispatchEmailChange(suggestion.domain)}
                  >
                    @{suggestion.domain}
                  </EmailTrackedButton>
                }
              />
            )}
          </div>
          <div className="m-b-1">
            <PasswordField
              value={password}
              pattern={USER_PASSWORD_REGEX_PATTERN}
              title={clientSideMessages.PASSWORD_LEN_VALIDATION}
              autoFocus={autoFocus === 'password'}
              subLabel={TRANSLATIONS.PASSWORD_SUBLABEL}
              onChange={onPasswordChange}
              aria-describedby="signup-form-error"
              aria-required={true}
              data-e2e="signup-password-input"
              onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                // eslint-disable-next-line no-param-reassign
                event.target.selectionStart = event.target.selectionEnd;
              }}
            />
          </div>
          {redirectUrl && <input type="hidden" name="redirectTo" value={redirectUrl} />}
          <div id="recaptcha-signup" data-size={!isCheckbox && 'invisible'} />
          {showCheckboxTermsOfUse && <TermsOfUseCheckbox ref={touCheckboxRef} data-testid="signup-form-tou-checkbox" />}
          <TrackedButton
            trackingName="signup_form_submit_button"
            rootClassName="m-t-1"
            disabled={isSignupBtnDisabled}
            type="primary"
            label={TRANSLATIONS.SIGN_UP}
            htmlAttributes={{
              'data-js': 'submit',
              'data-e2e': 'signup-form-submit-button',
              'data-catchpoint': 'signup-form-submit-button',
            }}
          />
        </Box>
      </form>
      <div className={enableSSOLogin ? '' : 'p-t-1'}>
        <AuthFormFooter authMode={CURRENT_AUTH_MODE} enableSSOLogin={enableSSOLogin} />
      </div>
    </Box>
  );
};
SignupForm.contextTypes = {
  _eventData: PropTypes.object.isRequired,
};

export default compose(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'user_account',
      page: 'signup',
    },
  }))
  // @ts-expect-error TS7016 TODO: SignupForm[has incompatible types
)(SignupForm);
